// ============================================
// DistributorProgram — Vista principal del panel de socio.
// Orden: hero implícito en parent → CTA pedido → masonry de regalos por nivel →
// carousel de productos más solicitados → progreso + estado de pedido.
// Las secciones de Mi cuenta / Historial / Devoluciones / Comunicaciones viven
// en sus propias rutas del menú; aquí sólo dejamos lo de operación rápida.
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCustomer } from '../../context/CustomerContext';
import {
  getDistributorTiers,
  getMyDistributorStatus,
  getMyDistributorProgress,
  getMyRecentOrders,
  getOrderInvoice,
  getPopularProducts,
  downloadInvoiceFile,
} from '../../services/medusa/distributor';
import InvoiceModal from './InvoiceModal';
import { getTracking } from '../../services/medusa/tracking';
import { DISTRIBUTOR_MIN_CART_TOTAL } from '../../hooks/useDistributorProgress';
import { useProducts } from '../../hooks/useProducts';
import ProductCarousel from '../ProductCarousel';
import LoadingDots from '../shop/LoadingDots';
import './DistributorProgram.css';
import { DISTRIBUTOR_TIER_IMAGES } from './distributorTierImages';

const fmt = (n) =>
  '$' + Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 });

// Medusa v2 expone montos en minor units (centavos). Los normalizamos a major
// para mostrarlos consistentemente con el resto del front.
// Medusa v2 ya devuelve pesos; se conserva el helper por claridad.
const minorToMajor = (n) => Number(n || 0);

// Mapea el fulfillment_status de Medusa al índice del stepper (0..3).
// El stepper tiene 4 pasos: Recibido · Procesando · Enviado · Entregado.
const STEPS = ['Recibido', 'Procesando', 'Enviado', 'Entregado'];
function stepIndexFromOrder(order) {
  if (!order) return -1;
  const fs = order.fulfillment_status;
  if (fs === 'delivered' || fs === 'partially_delivered') return 3;
  if (fs === 'shipped' || fs === 'partially_shipped') return 2;
  if (fs === 'fulfilled' || fs === 'partially_fulfilled') return 1;
  return 0; // not_fulfilled o cualquier otro estado inicial.
}

function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function DistributorProgram() {
  const { token } = useCustomer() || {};
  const [status, setStatus] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [progress, setProgress] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [popularIds, setPopularIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [carouselSlot, setCarouselSlot] = useState(null);
  const { products } = useProducts();
  const latestOrder = recentOrders[0] || null;

  // El carousel se renderiza (por portal) en un slot de ancho completo que
  // el panel coloca fuera de la columna de navegación.
  useEffect(() => {
    setCarouselSlot(document.getElementById('partner-carousel-slot'));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [s, t, p, orders, popular] = await Promise.all([
          token ? getMyDistributorStatus(token).catch(() => null) : null,
          getDistributorTiers().catch(() => ({ tiers: [] })),
          token ? getMyDistributorProgress(token).catch(() => null) : null,
          token ? getMyRecentOrders(token, 5).catch(() => []) : [],
          getPopularProducts({ limit: 10 }).catch(() => []),
        ]);
        if (cancelled) return;
        setStatus(s);
        const sorted = (t?.tiers || [])
          .slice()
          .sort((a, b) => Number(a.min_amount) - Number(b.min_amount));
        setTiers(sorted);
        setProgress(p);
        setRecentOrders(orders);
        setPopularIds(popular.map((r) => r.product_id));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Carga la factura previa de la última orden (si ya fue emitida) para
  // mostrar directamente los botones de descarga sin re-emitir.
  useEffect(() => {
    if (!token || !latestOrder?.id) {
      setInvoice(null);
      return;
    }
    let cancelled = false;
    getOrderInvoice(token, latestOrder.id)
      .then((inv) => {
        if (!cancelled) setInvoice(inv);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, latestOrder?.id]);

  // ─── Memos · arriba de los early-returns para no romper rules-of-hooks ─
  const productsById = useMemo(() => {
    const map = new Map();
    for (const p of products || []) map.set(p.id, p);
    return map;
  }, [products]);

  const reorderProducts = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const order of recentOrders) {
      for (const item of order?.items || []) {
        const pid = item.product_id;
        if (!pid || seen.has(pid)) continue;
        const full = productsById.get(pid);
        if (full) {
          seen.add(pid);
          out.push(full);
        }
      }
    }
    return out;
  }, [recentOrders, productsById]);

  // Top de productos populares (más solicitados por la red de distribuidoras).
  // Se usa como fallback del carousel cuando la socia no tiene historial,
  // así nunca aparece vacío.
  const popularProducts = useMemo(() => {
    const out = [];
    for (const pid of popularIds) {
      const full = productsById.get(pid);
      if (full) out.push(full);
    }
    return out;
  }, [popularIds, productsById]);

  // Carousel · siempre lleno. Empieza por el historial reciente de la socia
  // (reorder rápido), luego popular por la red, y como último recurso completa
  // con el catálogo. Dedup global por id, tope 10 cards.
  const carouselProducts = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (p) => {
      if (!p?.id || seen.has(p.id) || out.length >= 10) return;
      seen.add(p.id);
      out.push(p);
    };
    reorderProducts.forEach(push);
    popularProducts.forEach(push);
    (products || []).forEach(push);
    return out;
  }, [reorderProducts, popularProducts, products]);

  // Últimos regalos canjeados — line items con metadata.distributor_gift=true
  // en las órdenes recientes. Dedup por product_id, máximo 3.
  const recentGifts = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const order of recentOrders) {
      for (const item of order?.items || []) {
        if (!item?.metadata?.distributor_gift) continue;
        const pid = item.product_id;
        if (!pid || seen.has(pid)) continue;
        seen.add(pid);
        out.push({
          product_id: pid,
          title: item.title || productsById.get(pid)?.name || 'Regalo',
          thumbnail:
            item.thumbnail || productsById.get(pid)?.image || null,
        });
        if (out.length >= 3) return out;
      }
      if (out.length >= 3) break;
    }
    return out;
  }, [recentOrders, productsById]);

  const application = status?.application;
  const isApproved = status?.is_distributor;

  // ─── Estados previos a aprobación ─────────────────────────────────────
  if (loading) {
    return (
      <div className="dist-panel">
        <LoadingDots overlay={false} />
      </div>
    );
  }

  if (!isApproved && application?.status === 'pending') {
    return (
      <div className="dist-panel dist-panel--state">
        <span className="dist-panel__badge dist-panel__badge--pending">
          En revisión
        </span>
        <h2 className="dist-panel__state-title">Programa distribuidores</h2>
        <p>
          Tu solicitud está en revisión. Te avisamos por correo cuando la
          aprobemos.
        </p>
      </div>
    );
  }

  if (!isApproved && application?.status === 'rejected') {
    return (
      <div className="dist-panel dist-panel--state">
        <span className="dist-panel__badge dist-panel__badge--rejected">
          Solicitud rechazada
        </span>
        <h2 className="dist-panel__state-title">Programa distribuidores</h2>
        <p>
          Si crees que es un error o quieres reactivar el proceso, escríbenos
          a hola@physiology.mx.
        </p>
      </div>
    );
  }

  if (!isApproved) {
    return (
      <div className="dist-panel dist-panel--state">
        <h2 className="dist-panel__state-title">Programa distribuidores</h2>
        <p>
          Aún no eres parte del programa. Solicita acceso para recibir precios
          exclusivos, regalos por monto y un panel propio.
        </p>
        <a href="/distribuidores" className="dist-btn dist-btn--solid">
          Solicitar acceso
        </a>
      </div>
    );
  }

  // Progreso real desde el backend (acumulado en pedidos pagados de la temporada).
  // El endpoint ya devuelve `accumulated` normalizado en MAJOR units (pesos)
  // para que la comparación con tier.min_amount sea consistente.
  const accumulated = Number(progress?.accumulated || 0);
  const currentTier = progress?.current_tier || null;
  const nextTierFromProgress = progress?.next_tier || null;
  const fallbackNextTier = tiers.find(
    (t) => Number(t.min_amount) > accumulated
  ) || tiers[0];
  const nextTier = nextTierFromProgress || fallbackNextTier;
  const tierGoal = Number(
    nextTier?.min_amount || DISTRIBUTOR_MIN_CART_TOTAL
  );
  const progressPct = tierGoal
    ? Math.min(100, Math.round((accumulated / tierGoal) * 100))
    : 0;
  const remaining = Math.max(0, tierGoal - accumulated);
  const seasonLabel = progress?.season?.label || null;
  const daysLeft = daysUntil(progress?.season?.end);

  // carouselProducts memoizado vive arriba — su useMemo está colocado junto
  // con los demás antes del early-return (rules-of-hooks). Aquí sólo derivamos
  // el título.
  const carouselTitle = reorderProducts.length
    ? 'Tus productos recientes'
    : 'Los productos más solicitados por la red';
  const carouselSubtitle = reorderProducts.length
    ? 'Empieza por lo que ya compraste · resto, lo que más mueven otras socias'
    : 'Lo que más mueven las distribuidoras esta temporada';

  // Imágenes editoriales fijas por slot · una distinta por celda para que
  // se puedan reemplazar fácilmente cuando lleguen los renders finales.
  // Ignoran el thumbnail del tier a propósito (es el render editorial el
  // que manda la estética del grid, no la foto del producto de regalo).
  const slotImages = DISTRIBUTOR_TIER_IMAGES;

  return (
    <div className="dist-panel">
      {/* ─── Niveles del programa · Masonry estilo bazaar ─────────────── */}
      <section className="dist-levels">
        <header className="dist-section-head">
          <h2 className="dist-section-head__title">Niveles del programa</h2>
          <p className="dist-section-head__subtitle">
            Cuanto más completes tu pedido, más obsequios recibes
          </p>
        </header>
        <div className="dist-masonry">
          {Array.from({ length: 5 }).map((_, slot) => {
            const tier = tiers[slot];
            const slotClass = `dist-masonry__cell--slot-${slot + 1}`;
            const bgImage = slotImages[slot];
            return (
              <article
                key={tier?.id || `slot-${slot}`}
                className={`dist-masonry__cell ${slotClass}`}
                style={{ backgroundImage: `url(${bgImage})` }}
              >
                <div className="dist-masonry__scrim" aria-hidden="true" />
                <div className="dist-masonry__content">
                  <span className="dist-masonry__badge">
                    {tier
                      ? `Desde ${fmt(tier.min_amount)}`
                      : 'Próximo nivel'}
                  </span>
                  <h3 className="dist-masonry__title">
                    {tier?.label || 'Próximamente'}
                  </h3>
                  {tier?.description ? (
                    <p className="dist-masonry__lead">{tier.description}</p>
                  ) : (
                    <p className="dist-masonry__lead">
                      Más obsequios al subir de nivel.
                    </p>
                  )}
                  <a href="/shop" className="dist-masonry__cta">
                    Conocer
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ─── Acción principal · Tu pedido de Socio ───────────────────── */}
      <section className="dist-cta">
        <div className="dist-cta__copy">
          <p className="dist-cta__eyebrow">Tu pedido de Socio</p>
          <h2 className="dist-cta__title">Iniciar pedido</h2>
          <p className="dist-cta__lead">
            Todo pedido debe pasar por tu panel. Los precios de socio se
            aplican al instante y el pedido queda registrado para facturación.
          </p>
        </div>
        <a href="/shop" className="dist-cta__btn">
          <span>Iniciar pedido</span>
          <span className="dist-cta__arrow" aria-hidden="true">→</span>
        </a>
      </section>

      {/* ─── Progreso al regalo · Estado del pedido ──────────────────── */}
      <section className="dist-bottomgrid">
        <article className="dist-card dist-card--progress">
          <header className="dist-card__head dist-card__head--with-meta">
            <div>
              <h2 className="dist-card__title">Progreso al siguiente regalo</h2>
              <p className="dist-card__subtitle">
                {currentTier
                  ? `Estás en ${currentTier.label}. Acumula para subir de nivel.`
                  : 'Acumula durante la temporada para subir de nivel'}
              </p>
            </div>
            {seasonLabel && (
              <span className="dist-season-chip" title={`Cierra ${new Date(progress?.season?.end).toLocaleDateString('es-MX')}`}>
                <span className="dist-season-chip__label">{seasonLabel}</span>
                {daysLeft !== null && (
                  <span className="dist-season-chip__count">
                    {daysLeft === 0
                      ? 'Cierra hoy'
                      : `${daysLeft} ${daysLeft === 1 ? 'día' : 'días'}`}
                  </span>
                )}
              </span>
            )}
          </header>
          <div className="dist-progress">
            <div className="dist-progress__nums">
              <span className="dist-progress__current">{fmt(accumulated)}</span>
              <span className="dist-progress__goal">de {fmt(tierGoal)}</span>
            </div>
            <div className="dist-progress__bar">
              <div
                className="dist-progress__fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="dist-progress__meta">
              <span>
                Faltan <strong>{fmt(remaining)}</strong>
              </span>
              <span>
                Próximo regalo:{' '}
                <strong>{nextTier?.label || 'Pendiente'}</strong>
              </span>
            </div>
          </div>

          {recentGifts.length > 0 && (
            <div className="dist-gifts">
              <p className="dist-gifts__label">Ya canjeaste</p>
              <ul className="dist-gifts__list">
                {recentGifts.map((g) => (
                  <li key={g.product_id} className="dist-gifts__item" title={g.title}>
                    {g.thumbnail ? (
                      <img src={g.thumbnail} alt={g.title} />
                    ) : (
                      <span className="dist-gifts__placeholder" aria-hidden="true" />
                    )}
                    <span className="dist-gifts__name">{g.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>

        <article className="dist-card dist-card--orderstate">
          <header className="dist-card__head dist-card__head--with-meta">
            <div>
              <h2 className="dist-card__title">Pedido en curso</h2>
              <p className="dist-card__subtitle">
                {latestOrder
                  ? `Pedido #${latestOrder.display_id || latestOrder.id?.slice(-6)} · ${new Date(latestOrder.created_at).toLocaleDateString('es-MX')}`
                  : 'Aún no tienes pedidos registrados'}
              </p>
            </div>
            {latestOrder?.total != null && (
              <span
                className="dist-order-chip"
                title="Lo que este pedido suma al acumulado de la temporada"
              >
                <span className="dist-order-chip__label">Esta compra suma</span>
                <span className="dist-order-chip__amount">
                  {fmt(minorToMajor(latestOrder.total))}
                </span>
              </span>
            )}
          </header>
          {(() => {
            const stepIdx = stepIndexFromOrder(latestOrder);
            const tracking = getTracking(latestOrder);
            return (
              <>
                <ol className="dist-stepper">
                  {STEPS.map((label, idx) => {
                    const cls =
                      idx < stepIdx
                        ? 'dist-stepper__step--done'
                        : idx === stepIdx
                        ? 'dist-stepper__step--active'
                        : '';
                    return (
                      <li key={label} className={`dist-stepper__step ${cls}`}>
                        <span className="dist-stepper__dot" />
                        <span className="dist-stepper__label">{label}</span>
                      </li>
                    );
                  })}
                </ol>
                <div className="dist-orderstate__foot">
                  <span>
                    Guía de envío:{' '}
                    {tracking?.url ? (
                      <a
                        href={tracking.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dist-link"
                      >
                        {tracking.number || 'Rastrear'}
                      </a>
                    ) : (
                      <strong>{tracking?.number || '—'}</strong>
                    )}
                  </span>
                  {latestOrder && (
                    <a
                      href={`/account/orders/${latestOrder.id}`}
                      className="dist-link"
                    >
                      Ver detalle
                    </a>
                  )}
                </div>

                {latestOrder && (
                  <div className="dist-invoice">
                    {invoice ? (
                      <div className="dist-invoice__downloads">
                        <span className="dist-invoice__label">
                          Factura {invoice.series ? `${invoice.series}-` : ''}
                          {invoice.folio_number || invoice.id?.slice(-6)}
                        </span>
                        <button
                          type="button"
                          className="dist-invoice__btn"
                          onClick={() =>
                            downloadInvoiceFile(
                              token,
                              latestOrder.id,
                              'pdf',
                              `factura-${latestOrder.display_id || latestOrder.id}.pdf`
                            ).catch((err) => alert(err.message))
                          }
                        >
                          PDF
                        </button>
                        <button
                          type="button"
                          className="dist-invoice__btn"
                          onClick={() =>
                            downloadInvoiceFile(
                              token,
                              latestOrder.id,
                              'xml',
                              `factura-${latestOrder.display_id || latestOrder.id}.xml`
                            ).catch((err) => alert(err.message))
                          }
                        >
                          XML
                        </button>
                        {invoice.verification_url && (
                          <a
                            href={invoice.verification_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dist-invoice__btn dist-invoice__btn--ghost"
                          >
                            Verificar en SAT
                          </a>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="dist-invoice__cta"
                        onClick={() => setInvoiceModalOpen(true)}
                      >
                        Solicitar factura
                      </button>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </article>
      </section>

      {/* ─── Carousel · full-width al fondo (portal fuera de la columna) ── */}
      {carouselSlot &&
        carouselProducts.length > 0 &&
        createPortal(
          <div className="dist-panel dist-carousel-portal">
            <section className="dist-carousel-wrap">
              <ProductCarousel
                products={carouselProducts}
                title={carouselTitle}
                subtitle={carouselSubtitle}
              />
            </section>
          </div>,
          carouselSlot
        )}

      <InvoiceModal
        open={invoiceModalOpen}
        orderId={latestOrder?.id}
        token={token}
        onClose={() => setInvoiceModalOpen(false)}
        onSuccess={(inv) => {
          setInvoice(inv);
          setInvoiceModalOpen(false);
        }}
      />
    </div>
  );
}
